import { useCallback, useEffect, useRef } from "react";
import { useFeedback } from "../../feedback/useFeedback";
import { useUiLanguage } from "../../ui/uiText";
import { discardChangesTranslations } from "../discardChangesTranslations";

/** Alle expliziten Schließwege teilen eine Rückfrage; erfolgreiches Speichern schließt weiterhin direkt. */
export function useDrawerCloseGuard(onClose: () => void, dirty: boolean | (() => boolean), canDismiss = true, open = true) {
  const feedback = useFeedback();
  const language = useUiLanguage();
  const pending = useRef(false);
  const generation = useRef(0);
  const latest = useRef({ onClose, canDismiss, open });
  const confirmation = useRef<{ destroy: () => void } | null>(null);
  useEffect(() => { latest.current = { onClose, canDismiss, open }; });
  const clearConfirmation = useCallback(() => {
    generation.current++;
    const current = confirmation.current;
    confirmation.current = null;
    pending.current = false;
    current?.destroy();
  }, []);
  useEffect(() => {
    if (!open) clearConfirmation();
    return clearConfirmation;
  }, [open, clearConfirmation]);
  const requestClose = () => {
    if (!canDismiss || !open) return;
    if (!(typeof dirty === "function" ? dirty() : dirty)) { onClose(); return; }
    if (pending.current) return;
    pending.current = true;
    const currentGeneration = ++generation.current;
    const labels = discardChangesTranslations[language] ?? discardChangesTranslations.de;
    confirmation.current = feedback.confirm({
      title: labels.title,
      content: labels.content,
      okText: labels.discard,
      cancelText: labels.keepEditing,
      autoFocusButton: "cancel",
      onOk: () => { if (generation.current === currentGeneration && latest.current.canDismiss && latest.current.open) return latest.current.onClose(); },
      afterClose: () => { if (generation.current === currentGeneration) { pending.current = false; confirmation.current = null; } },
    });
  };
  return requestClose;
}
